import { readConsent } from "./consent";

/**
 * The theme's predefined analytics events — ONE catalog, the same on every
 * portal (decisions.md D12). Sent to GA4 only, only after consent; a portal
 * without a measurement ID sends nothing. Names are GA4's recommended ones
 * where GA has one (`sign_up`, `login`, `share`, `begin_checkout`,
 * `purchase`, `view_promotion`, `select_promotion`, and the `video_*` trio
 * with enhanced measurement's own parameters), so they land in GA's standard
 * reports; the rest are ours. Never a person's name, email or any typed text.
 *
 * GA admin, once per property — register these as event-scoped custom
 * dimensions, or GA receives them and reports none of them:
 * `content_type`, `content_id`, `content_category`, `author`,
 * `published_date`, `sponsored`, `sponsor_name`, `percent`, `link_domain`,
 * `placement`. `content_group`, `method`, the promotion and video parameters
 * are GA's built-ins.
 *
 * Sponsored content, the report a sponsor gets: teaser impressions and clicks
 * (`view_promotion` / `select_promotion`, promotion_id = the content id), opens
 * (`content_view` where `sponsored` ≠ "none" — "Total users" is people),
 * reading time (GA's engagement time), read-through (`read_progress`),
 * clicks to the sponsor (`sponsored_link_click`) and `share`.
 */

/** What every content event says about the item. */
export type ContentRef = {
  /** news · announcement · article · course · event · ott · podcast */
  content_type: string;
  content_id: string;
  /** "sponsored" | "partner" | "none" — never omitted, so a filter on it works. */
  sponsored: "sponsored" | "partner" | "none";
  sponsor_name?: string;
};

export type ContentView = ContentRef & {
  content_title: string;
  /** GA's built-in content group: the section (news, articles, videos…). */
  content_group: string;
  content_category?: string;
  author?: string;
  /** YYYY-MM-DD */
  published_date?: string;
};

export type EventCatalog = {
  content_view: ContentView;
  read_progress: ContentRef & { percent: 25 | 50 | 75 | 100 };
  sponsored_link_click: ContentRef & { link_url: string; link_domain: string };
  view_promotion: SponsoredTeaser;
  select_promotion: SponsoredTeaser;
  share: { method: string; item_id: string };
  newsletter_signup: { placement: string };
  sign_up: { method: string };
  login: { method: string };
  begin_checkout: { currency: string; value: number };
  purchase: { transaction_id: string; currency: string; value: number };
  video_start: VideoParams;
  video_progress: VideoParams & { video_percent: 10 | 25 | 50 | 75 };
  video_complete: VideoParams;
};

/** A sponsored card in a list — GA's promotion fields. */
export type SponsoredTeaser = {
  promotion_id: string;
  promotion_name: string;
  creative_name: "sponsored" | "partner";
  /** The page path the card was seen on. */
  creative_slot: string;
  sponsor_name?: string;
};

export type VideoParams = {
  video_provider: "lecturehub";
  video_title: string;
  video_url: string;
  video_duration?: number;
  video_current_time?: number;
};

type Send = (event: string, params: Record<string, unknown>) => void;

// GA starts in a layout effect that runs AFTER the page's own effects, so an
// event fired on mount can arrive before the tag: hold it until GA connects.
// Only consented events are held, and only in memory.
let send: Send | null = null;
const pending: Array<[string, Record<string, unknown>]> = [];

export function connectAnalytics(fn: Send) {
  send = fn;
  for (const [event, params] of pending.splice(0)) fn(event, params);
}

export function disconnectAnalytics() {
  send = null;
  pending.length = 0;
}

/**
 * Sends one catalog event. False when it was not sent because the visitor has
 * not consented — a caller that must count the current page (content_view)
 * tries again when consent arrives.
 */
export function trackEvent<E extends keyof EventCatalog>(
  event: E,
  params: EventCatalog[E],
): boolean {
  if (typeof window === "undefined" || readConsent() !== "granted") return false;
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
  if (send) send(event, clean);
  else if (pending.length < 30) pending.push([event, clean]);
  return true;
}

/** The read-depth marks a scroll position has newly passed, given the highest
 *  mark already sent. */
export function newReadMarks(fraction: number, sent: number): Array<25 | 50 | 75 | 100> {
  return ([25, 50, 75, 100] as const).filter((m) => m > sent && fraction * 100 >= m);
}

/** The video-progress marks newly passed (GA's enhanced-measurement set). */
export function newVideoMarks(fraction: number, sent: number): Array<10 | 25 | 50 | 75> {
  return ([10, 25, 50, 75] as const).filter((m) => m > sent && fraction * 100 >= m);
}

/** How far through a region the reader is, measured at the viewport's bottom
 *  edge (the last line read, not the last line shown). 1 when it fits. */
export function readFraction(top: number, height: number, viewport: number): number {
  const total = height - viewport;
  return total > 0 ? Math.min(1, Math.max(0, -top / total)) : 1;
}

/** `ContentRef.sponsored` from the sponsorship helper's result. */
export function sponsoredParam(
  s: { kind: "sponsored" | "partner"; name: string | null } | null,
): Pick<ContentRef, "sponsored" | "sponsor_name"> {
  return s ? { sponsored: s.kind, sponsor_name: s.name ?? undefined } : { sponsored: "none" };
}
